import React from "react";
import { savedSLanguage } from "@/pages/SEmbed/useSEmbed";
import { en, type MessageKey, type PluralKey } from "./messages/en";
import { ar } from "./messages/ar";

export type { MessageKey, PluralKey };
export type Language = "en" | "ar";
export type Direction = "ltr" | "rtl";
type Params = Record<string, string | number>;

const dictionaries: Record<Language, Record<string, string>> = { en, ar };

// Another tab picking a language updates open pages; this tab rereads it on every render.
function subscribe(onChange: () => void) {
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
}

/** The interface language the user picked in S, saved in this browser. */
export function useLanguage(): Language {
  return React.useSyncExternalStore(subscribe, savedSLanguage);
}

function interpolate(message: string, params?: Params): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export function createTranslator(language: Language) {
  const dictionary = dictionaries[language];
  // English keeps the browser's regional formats (en-GB dates, say); Arabic matches S's `ar`.
  const locale =
    language === "ar" ? "ar" : (navigator.languages.find((tag) => /^en\b/i.test(tag)) ?? "en");
  const pluralRules = new Intl.PluralRules(locale);
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat(locale, options).format(value);
  /** The message for `key`, with each `{name}` replaced by `params.name`. */
  const t = (key: MessageKey, params?: Params) => interpolate(dictionary[key] ?? en[key], params);
  /** The plural form of `key` for `count`, which fills `{count}` as a localized number. */
  const tn = (key: PluralKey, count: number, params?: Params) =>
    interpolate(dictionary[`${key}.${pluralRules.select(count)}`] ?? dictionary[`${key}.other`], {
      count: formatNumber(count),
      ...params,
    });
  const dir: Direction = language === "ar" ? "rtl" : "ltr";
  return { language, dir, locale, t, tn, formatNumber };
}

export type Translator = ReturnType<typeof createTranslator>;

/** Messages, direction and number formats in the user's interface language. */
export function useT(): Translator {
  const language = useLanguage();
  return React.useMemo(() => createTranslator(language), [language]);
}

/**
 * Sets the page's `dir` and `lang` while the caller is mounted, so portals (dialogs, menus,
 * tooltips) follow the language too, and restores them afterwards.
 */
export function useDocumentLanguage({ language, dir }: Pick<Translator, "language" | "dir">) {
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const previousDir = root.getAttribute("dir");
    const previousLang = root.getAttribute("lang");
    root.setAttribute("dir", dir);
    root.setAttribute("lang", language);
    return () => {
      restoreAttribute(root, "dir", previousDir);
      restoreAttribute(root, "lang", previousLang);
    };
  }, [language, dir]);
}

function restoreAttribute(element: Element, name: string, value: string | null) {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
}
